import { createDomain } from 'effector'
import { useStoreMap } from 'effector-react'
import * as z from 'zod'
import { Employee } from '~/shared/api'
import { Option, OptionEnum } from '~/shared/config/constants'
import { LocaleEnum, TimezoneCode } from '~/shared/config/enums'
import { createCache } from '~/shared/lib/mapCacheFactory'

export const formSchema = Employee.schema.pick({
  name: true,
  email: true,
  phone: true,
  rolesIds: true,
  regionsIds: true,
  telegramUserId: true,
  timezone: true,
  locale: true,
  carRentalsIds: true,
})
export type SchemaType = z.infer<typeof formSchema>
export type FormValues = Omit<
  SchemaType,
  'regionsIds' | 'rolesIds' | 'carRentalsIds' | 'timezone' | 'locale'
> & {
  regionsIds: UniqueId[] | Option[]
  rolesIds?: UniqueId[] | Option[]
  carRentalsIds?: UniqueId[] | Option[]
  timezone: TimezoneCode | OptionEnum<TimezoneCode> | null
  locale: LocaleEnum | OptionEnum<LocaleEnum> | null
}

export const domain = createDomain('entities.taxipark.employee')

export const requestFx = domain.createEffect<UniqueId, Employee>({
  handler: fetchEmployeeWithRelations,
})

export const saveFx = domain.createEffect<Employee, Employee>({
  async handler(employee) {
    await employee.save()
    return fetchEmployeeWithRelations(employee.getApiId() as UniqueId)
  },
})

const {
  $cache: $employeesCache,
  useCache: useEmployeeCache,
  updateCache,
} = createCache<Employee>({
  domain,
  getEntityId: (employee) => employee.getApiId() as UniqueId,
})
export { $employeesCache, useEmployeeCache }

$employeesCache
  .on(requestFx.doneData, (cache, employee) => updateCache(cache, [employee]))
  .on(saveFx.doneData, (cache, employee) =>
    updateCache(cache, [employee], true),
  )

export const $employeesError = domain
  .createStore<Record<UniqueId, Error>>({})
  .on([requestFx.fail], (store, { error, params: id }) => ({
    [id]: error,
    ...store,
  }))
export const useEmployeeError = (id: UniqueId) =>
  useStoreMap($employeesError, (errors) => errors[id])

async function fetchEmployeeWithRelations(id: UniqueId) {
  const response = await Employee.with('regions')
    .with('roles')
    .with('carRentals')
    .find(id)
  return response.getData() as Employee
}
